/**
 * Shared API auth plumbing — the single token source for every service.
 *
 * Token resolution order:
 * 1. The Frontegg session's access token (when the provider is mounted —
 *    production's hosted-login front door).
 * 2. Legacy localStorage tokens ('jwt_token' / 'auth_token') — local dev,
 *    tests, and the agency store-switch flow that still writes them.
 *
 * Replaces fourteen per-service copies of getAuthToken/createHeaders.
 */

import { ContextHolder } from '@frontegg/react';

function fronteggAccessToken(): string | null {
  try {
    // v7 exposes the holder behind .default(); older majors were static.
    const holderFactory = (
      ContextHolder as unknown as { default?: () => { getAccessToken?: () => string | null } }
    ).default;
    const holder = holderFactory
      ? holderFactory()
      : (ContextHolder as unknown as { getAccessToken?: () => string | null });
    return holder?.getAccessToken?.() ?? null;
  } catch {
    // Provider not mounted (legacy mode) — fall through to localStorage.
    return null;
  }
}

/**
 * The current JWT, Frontegg-first with legacy localStorage fallback.
 */
export function getAuthToken(): string | null {
  return (
    fronteggAccessToken() ||
    localStorage.getItem('jwt_token') ||
    localStorage.getItem('auth_token')
  );
}

/**
 * Standard JSON request headers with the Authorization bearer when present.
 */
export function createHeaders(): HeadersInit {
  const token = getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}
